import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Link, Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { Plus, Trash, PencilSimple, ArrowUp, ArrowDown, ArrowsClockwise, Eye, Handshake, ArrowSquareOut } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_STEP = { label: '', short: '', icon: 'Sparkle', name: '', crop_cx: 50, crop_cy: 50 };
const EMPTY = {
  key: '', name: '', tagline: '', description: '', accent: '#2D5A4C', image: '',
  order: 99, active: true,
  steps: [ { ...EMPTY_STEP }, { ...EMPTY_STEP }, { ...EMPTY_STEP }, { ...EMPTY_STEP } ],
};

export default function MissionsAdminPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const isAdmin = user && ['super_admin', 'admin', 'direction'].includes(user.role);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/missions-registry`, { withCredentials: true });
      setItems((r.data?.items || []).sort((a, b) => (a.order ?? 99) - (b.order ?? 99)));
    } catch {
      toast.error('Erreur de chargement des missions');
    }
    setLoading(false);
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!user) return null;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, steps: EMPTY.steps.map((s) => ({ ...s })) }); setDialogOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({
      ...EMPTY,
      ...m,
      steps: (m.steps || []).map((s) => ({ ...EMPTY_STEP, ...s })),
    });
    setDialogOpen(true);
  };

  const setStep = (i, patch) => setForm((f) => ({ ...f, steps: f.steps.map((s, j) => j === i ? { ...s, ...patch } : s) }));

  const save = async () => {
    if (!form.key || form.key.length < 2) return toast.error('Clé requise (ex: agro, logi)');
    if (!/^[a-z0-9_-]+$/.test(form.key)) return toast.error('Clé : minuscules, chiffres, - ou _ uniquement');
    if (!form.name || form.name.length < 2) return toast.error('Nom requis');
    if (!form.image) return toast.error('URL image illustration requise');
    if (form.steps.length !== 4) return toast.error('Chaque mission doit avoir 4 étapes');
    for (let i = 0; i < 4; i++) {
      if (!form.steps[i].label) return toast.error(`Étape ${i + 1} : libellé requis`);
    }
    setSaving(true);
    try {
      if (editing) {
        const { key, ...body } = form;
        await axios.patch(`${API}/api/missions-registry/${editing.key}`, body, { withCredentials: true });
        toast.success('Mission mise à jour');
      } else {
        await axios.post(`${API}/api/missions-registry`, form, { withCredentials: true });
        toast.success('Mission créée');
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Erreur de sauvegarde';
      toast.error(detail);
    }
    setSaving(false);
  };

  const remove = async (m) => {
    if (!window.confirm(`Supprimer la mission "${m.name}" ? Action irréversible.`)) return;
    try {
      await axios.delete(`${API}/api/missions-registry/${m.key}`, { withCredentials: true });
      toast.success('Mission supprimée');
      await load();
    } catch { toast.error('Erreur suppression'); }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const [it] = next.splice(idx, 1);
    next.splice(target, 0, it);
    setItems(next);
    setReordering(true);
    try {
      await axios.post(`${API}/api/missions-registry/reorder`, { keys: next.map((x) => x.key) }, { withCredentials: true });
      toast.success('Ordre mis à jour');
    } catch {
      toast.error('Erreur ordre');
      await load();
    }
    setReordering(false);
  };

  const reseed = async () => {
    if (!window.confirm('Restaurer les 7 missions par défaut ? Toutes les missions actuelles seront supprimées.')) return;
    try {
      await axios.post(`${API}/api/missions-registry/reseed`, {}, { withCredentials: true });
      toast.success('Missions réinitialisées');
      await load();
    } catch { toast.error('Erreur reseed'); }
  };

  return (
    <div className="space-y-6" data-testid="missions-admin-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-[#1A2421] flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Handshake size={24} weight="duotone" className="text-[#2D5A4C]" />
            Missions publiques
          </h1>
          <p className="text-sm text-[#5E6D66] mt-1">
            Gérez les secteurs C.I.P.A.S.C.O.P. affichés sur la page publique d'accueil.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/#agro" target="_blank" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-[#E8E6E1] text-[#5E6D66] hover:bg-[#F5F4EF] transition-colors" data-testid="missions-preview-public">
            <ArrowSquareOut size={14} weight="bold" /> Aperçu public
          </Link>
          <Button onClick={reseed} variant="outline" className="border-[#E8E6E1]" data-testid="missions-reseed">
            <ArrowsClockwise size={14} weight="bold" className="mr-1.5" /> Restaurer 7 défauts
          </Button>
          <Button onClick={openNew} className="bg-[#2D5A4C] hover:bg-[#2D5A4C]/90 text-white" data-testid="missions-new-btn">
            <Plus size={16} weight="bold" className="mr-1.5" /> Nouvelle mission
          </Button>
        </div>
      </div>

      <Card className="border-[#E8E6E1]">
        <CardHeader>
          <CardTitle className="text-base">
            {loading ? 'Chargement…' : `${items.length} mission${items.length > 1 ? 's' : ''}`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[#5E6D66]"><Loader2 className="animate-spin" size={14} /> Chargement…</div>
          ) : items.length === 0 ? (
            <p className="text-sm text-[#5E6D66]">Aucune mission. Créez-en une ou restaurez les 7 défauts.</p>
          ) : (
            <ul className="divide-y divide-[#E8E6E1]" data-testid="missions-list">
              {items.map((m, i) => (
                <li key={m.key} className="flex items-center gap-4 py-3" data-testid={`mission-row-${m.key}`}>
                  <div className="w-14 h-14 rounded-lg flex-shrink-0 border border-[#E8E6E1] bg-cover bg-center" style={{ backgroundImage: `url(${m.image})` }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: m.accent }} />
                      <p className="font-bold text-[#1A2421] truncate">{m.name}</p>
                      <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-md bg-[#F5F4EF] text-[#5E6D66] border border-[#E8E6E1]">{m.key}</span>
                      {m.active === false && <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-md bg-red-50 text-red-700 border border-red-200">Inactive</span>}
                    </div>
                    <p className="text-xs text-[#5E6D66] truncate mt-0.5">{m.tagline}</p>
                    <p className="text-[11px] text-[#8A968F] mt-0.5">{(m.steps || []).length} étape(s)</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => move(i, -1)}
                      disabled={i === 0 || reordering}
                      title="Monter"
                      className="w-8 h-8 rounded-md border border-[#E8E6E1] text-[#5E6D66] hover:bg-[#F5F4EF] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                      data-testid={`mission-move-up-${m.key}`}
                    >
                      <ArrowUp size={14} weight="bold" />
                    </button>
                    <button
                      onClick={() => move(i, +1)}
                      disabled={i === items.length - 1 || reordering}
                      title="Descendre"
                      className="w-8 h-8 rounded-md border border-[#E8E6E1] text-[#5E6D66] hover:bg-[#F5F4EF] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                      data-testid={`mission-move-down-${m.key}`}
                    >
                      <ArrowDown size={14} weight="bold" />
                    </button>
                    <a
                      href={`/#${m.key}`}
                      target="_blank"
                      rel="noreferrer"
                      title="Voir sur la page publique"
                      className="w-8 h-8 rounded-md border border-[#E8E6E1] text-[#5E6D66] hover:bg-[#F5F4EF] flex items-center justify-center"
                      data-testid={`mission-view-${m.key}`}
                    >
                      <Eye size={14} weight="bold" />
                    </a>
                    <button
                      onClick={() => openEdit(m)}
                      title="Éditer"
                      className="w-8 h-8 rounded-md border border-[#E8E6E1] text-[#2D5A4C] hover:bg-[#F5F4EF] flex items-center justify-center"
                      data-testid={`mission-edit-${m.key}`}
                    >
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button
                      onClick={() => remove(m)}
                      title="Supprimer"
                      className="w-8 h-8 rounded-md border border-[#E8E6E1] text-[#EA3C3A] hover:bg-red-50 flex items-center justify-center"
                      data-testid={`mission-delete-${m.key}`}
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Modifier — ${editing.name}` : 'Nouvelle mission'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Clé (identifiant, ex: agro, logi)</Label>
                <Input
                  value={form.key}
                  onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase() })}
                  disabled={!!editing}
                  placeholder="ex: horticole"
                  data-testid="mission-form-key"
                />
              </div>
              <div>
                <Label>Nom affiché</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ex: AGRO'SCOP" data-testid="mission-form-name" />
              </div>
              <div className="md:col-span-2">
                <Label>Sous-titre / tagline</Label>
                <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="ex: Transformation agroalimentaire" />
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
              </div>
              <div>
                <Label>Couleur d'accent (hex)</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} className="w-10 h-10 rounded border border-[#E8E6E1] cursor-pointer" />
                  <Input value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} className="font-mono text-xs" />
                </div>
              </div>
              <div>
                <Label>Ordre d'affichage</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="md:col-span-2">
                <Label>URL illustration officielle</Label>
                <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://…" data-testid="mission-form-image" />
                {form.image && (
                  <div className="mt-2 w-full h-24 rounded-md border border-[#E8E6E1] bg-cover bg-center" style={{ backgroundImage: `url(${form.image})` }} />
                )}
              </div>
              <div className="flex items-center gap-2 md:col-span-2">
                <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} id="mission-active" />
                <Label htmlFor="mission-active" className="cursor-pointer">Mission active (visible sur la page publique)</Label>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-bold text-[#1A2421] uppercase tracking-widest mb-2">4 étapes de la mission</h3>
              <div className="space-y-3">
                {form.steps.map((s, i) => (
                  <div key={i} className="rounded-lg border border-[#E8E6E1] p-3 bg-[#F9F8F5]" data-testid={`step-form-${i}`}>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-[#5E6D66] mb-2">Étape {i + 1}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Input value={s.label} onChange={(e) => setStep(i, { label: e.target.value })} placeholder="Titre étape (ex: Réception & Tri)" />
                      <Input value={s.icon} onChange={(e) => setStep(i, { icon: e.target.value })} placeholder="Icône Phosphor (ex: Basket)" className="font-mono text-xs" />
                      <Input value={s.short} onChange={(e) => setStep(i, { short: e.target.value })} placeholder="Description courte" className="md:col-span-2" />
                      <Input value={s.name} onChange={(e) => setStep(i, { name: e.target.value })} placeholder="Nom coopérateur (ex: Marie-Claire)" />
                      <div className="flex items-center gap-2">
                        <Input type="number" min="0" max="100" value={s.crop_cx} onChange={(e) => setStep(i, { crop_cx: parseInt(e.target.value) || 0 })} placeholder="cx %" />
                        <Input type="number" min="0" max="100" value={s.crop_cy} onChange={(e) => setStep(i, { crop_cy: parseInt(e.target.value) || 0 })} placeholder="cy %" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#2D5A4C]/90 text-white" data-testid="mission-form-save">
              {saving ? <Loader2 className="animate-spin" size={14} /> : (editing ? 'Enregistrer' : 'Créer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
